import type { RiskRegisterResponseRow } from '@risksmart-app/drizzle/src/types';
import type { useGetRisksFlat } from 'src/hooks/queries/risk/useGetRisksFlat';

import type { ResolvedRating } from '@/ratings/useRiskRatingResolver';
import type { CollectionData } from '@/utils/collectionUtils';
import type { LinkItem } from '@/utils/table/hooks/useLinkArrayField';
import type { TrendIndicator } from '@/utils/trendCalculation';

type GetRisksFlatData = NonNullable<ReturnType<typeof useGetRisksFlat>['data']>;

// RSP-5796: `getRisksFlat` now selects the controls/actions/indicators detail
// (to populate the register Associations column on the flag-off GraphQL path),
// which would otherwise make those relations required on `RiskFields`. That
// type is reused by fixtures and the internal-audit / enterprise-risk pages with
// data that doesn't carry the relations, so omit them here. The register reads
// the detail via the tRPC-row bridge (`RiskLinkedRecords`) instead.
type RiskRegisterRow = CollectionData<GetRisksFlatData['risk'][number]>;
type RiskAppetiteSelection = Required<
  Pick<RiskRegisterRow['appetites'][number], 'appetite'>
> & {
  Id?: string;
};
type RiskAssessmentSelection = NonNullable<
  RiskRegisterRow['assessmentResults'][number]['riskAssessmentResult']
>;
type RiskAssessmentResultSelection = Omit<
  RiskRegisterRow['assessmentResults'][number],
  'riskAssessmentResult'
> & {
  riskAssessmentResult?:
    | (Omit<RiskAssessmentSelection, 'ConfigId'> &
        Partial<Pick<RiskAssessmentSelection, 'ConfigId'>>)
    | null;
};
type RiskScheduleSelection = Pick<
  NonNullable<RiskRegisterRow['schedule']>,
  | 'Frequency'
  | 'Id'
  | 'ManualDueDate'
  | 'StartDate'
  | 'TimeToCompleteUnit'
  | 'TimeToCompleteValue'
>;
type OptionalRiskSelection = Extract<
  'enterpriseRiskInstance' | 'modifiedByUser' | 'riskScore' | 'scheduleState',
  keyof RiskRegisterRow
>;

export type RiskFields = Omit<
  RiskRegisterRow,
  | 'actions'
  | 'appetites'
  | 'assessmentResults'
  | 'controls'
  | 'indicators'
  | 'schedule'
  | OptionalRiskSelection
> &
  Partial<Pick<RiskRegisterRow, OptionalRiskSelection>> & {
    appetites: RiskAppetiteSelection[];
    assessmentResults: RiskAssessmentResultSelection[];
    schedule?: RiskScheduleSelection | null;
  };

type RiskFieldsInput = Omit<RiskFields, 'appetites'> & {
  appetites: (Omit<RiskAppetiteSelection, 'appetite'> & {
    appetite?: RiskAppetiteSelection['appetite'];
  })[];
};

export const normaliseRiskFields = (risk: RiskFieldsInput): RiskFields => ({
  ...risk,
  appetites: risk.appetites.map((selection) => ({
    ...selection,
    appetite: selection.appetite ?? null,
  })),
});

// The risk register fetches via tRPC (`frontend.risk.register`) and maps the row
// into the GraphQL shape (see `mapTrpcRiskToGraphQL`). The linked-record
// relations (controls/actions/indicators) are carried on both fetch paths at
// runtime but are omitted from `RiskFields` (above), so their detail is typed
// from the tRPC row. RSP-5796: these power the opt-in Associations column.
export type RiskLinkedRecords = Pick<
  RiskRegisterResponseRow,
  'controls' | 'actions' | 'indicators'
>;

export type RiskRegisterFields = Omit<RiskFields, 'TestFrequency'> & {
  TierLabelled: string;
  UncontrolledRatingLabelled: string;
  ControlledRatingLabelled: string;
  ParentTitle: null | string;
  Associations: LinkItem[];
  UncontrolledRating: null | number;
  ControlledRating: null | number;
  // Pre-resolved so the register cells and dashboard stay consistent.
  UncontrolledRatingOption: ResolvedRating | undefined;
  ControlledRatingOption: ResolvedRating | undefined;
  // Dashboard-only: the rating option, but only when the score was assessed
  // under the current scoring config version; otherwise undefined so rating
  // distribution widgets count it as Unrated until re-rated. The register keeps
  // using the version-resolved options above.
  UncontrolledRatingOptionLatestOnly: ResolvedRating | undefined;
  ControlledRatingOptionLatestOnly: ResolvedRating | undefined;
  // Config version each score was assessed under, so the register resolves L/I
  // labels, colours and the score-cell colour against that version.
  InherentConfigId: null | string;
  ResidualConfigId: null | string;
  UncontrolledScore: null | number;
  ControlledScore: null | number;
  // When the `risk_score` row was last written; `null` when the risk has none,
  // which under an aggregation model means no score was ever calculated.
  RiskScoreModifiedAt: null | string;
  LinkedControlCount: number;
  LinkedIndicatorCount: number;
  UserName: null | string;
  ModifiedByUserName: null | string;
  ControlledLikelihoodValue: null | number;
  ControlledImpactValue: null | number;
  UncontrolledImpactValue: null | number;
  UncontrolledLikelihoodValue: null | number;
  ControlledLikelihoodColour: string | null;
  UncontrolledLikelihoodColour: string | null;
  ControlledImpactColour: string | null;
  UncontrolledImpactColour: string | null;
  UncontrolledLikelihood: string;
  ControlledLikelihood: string;
  ControlledImpact: string;
  LowerAppetiteLabelled: null | string;
  UpperAppetiteLabelled: null | string;
  AppetitePerformanceLabelled: null | string;
  AppetitePerformance?: null | string;
  UncontrolledImpact: string;
  TreatmentLabelled: null | string;
  StatusLabelled: null | string;
  SequentialIdLabel: null | string;
  allOwners: { label: string; id: string }[];
  allContributors: { label: string; id: string }[];
  allViewers: { label: string; id: string }[];
  ImpactPerformanceScore: null | number;
  NextTestDate: null | string;
  NextTestOverdueDate: null | string;
  LatestRatingDate: null | string;
  TestScheduleStatus: null | string;
  TestScheduleStatusLabelled: null | string;
  TestFrequency: null | string;
  UncontrolledRatingHistory: {
    rating: number;
    likelihood: number | null;
    impact: number | null;
    id: string;
    testDate: string;
    configId: string | null;
  }[];
  ControlledRatingHistory: {
    rating: number;
    likelihood: number | null;
    impact: number | null;
    id: string;
    testDate: string;
    configId: string | null;
  }[];
  UncontrolledRatingTrend: TrendIndicator | null;
  UncontrolledRatingTrendLabelled: string;
  ControlledRatingTrend: TrendIndicator | null;
  ControlledRatingTrendLabelled: string;
  Entity: string;
  EnterpriseRiskLabelled: string;
  ParentEnterpriseRiskTitle: string | null;
  ParentEnterpriseRiskId: string | null;
  T1CategoryTitle: string | null;
  T2CategoryTitle: string | null;
  OversightInherentRating: null | number;
  OversightInherentLikelihood: null | number;
  OversightInherentImpact: null | number;
  OversightInherentConfigId: null | string;
  OversightResidualRating: null | number;
  OversightResidualLikelihood: null | number;
  OversightResidualImpact: null | number;
  OversightResidualConfigId: null | string;
  OversightInherentRatingLabelled: null | string;
  OversightResidualRatingLabelled: null | string;
  OversightInherentScore: null | number;
  OversightResidualScore: null | number;
};
